'use client';

import * as React from 'react';
import {Carousel} from '@purpose/ui/components/carousel';
import {cn} from '@purpose/ui/lib/utils';
import {Plus} from 'lucide-react';
import {colors} from '@purpose/ui/styles/theme/colors';
import {TFunction} from '@purpose/ui/utils/helpers';

interface OrganisationCarouselCard {
  logo: string;
  name?: string;
  description?: string;
  subtitle?: string;
  id: number;
  organisation_id?: number;
  organisaton_id?: number;
  category_name?: string;
  category_key?: string;
  organisation_name?: string;
  is_multiple_organisation?: boolean;
  organisations?: {id: number; name: string; logo: string | null}[];
}

interface OrganisationsCarouselProps {
  title?: string;
  onCardClick?: (id: number, orgId?: number) => void;
  cards?: OrganisationCarouselCard[];
  onTitleClick?: () => void;
  buttonIconColor?: string;
  titleColor?: string;
  getDescription?: (card: OrganisationCarouselCard) => string | undefined;
  roundCards?: boolean;
  categoryDesign?: boolean;
  withPlaceholderCards?: boolean;
  addOrganisation?: () => void;
  t: TFunction;
  overtitle?: (b: any) => React.JSX.Element;
  renderLastCard?: () => React.ReactNode;
}

const OrganisationsCarousel = React.forwardRef<
  HTMLDivElement,
  OrganisationsCarouselProps
>(
  (
    {
      title,
      cards,
      onTitleClick,
      onCardClick,
      titleColor = 'text-foreground',
      buttonIconColor,
      getDescription,
      roundCards = false,
      categoryDesign = false,
      withPlaceholderCards = false,
      addOrganisation,
      t,
      renderLastCard,
      overtitle = () => <></>
    },
    ref
  ) => {
    if (!cards?.length) return null;

    const placeholderProps = {
      withTitle: true,
      withSubtitle: true,
      description: t('OrganisationPlaceholderCard.title'),
      buttonProps: {
        title: t('OrganisationPlaceholderCard.btn'),
        Icon: <Plus className="h-4 w-4" style={{color: colors.primary[70]}} />,
        onClick: () => addOrganisation?.()
      }
    };

    return (
      <div className="w-full overflow-hidden" ref={ref}>
        {title && (
          <button
            className={cn({
              'cursor-pointer': !!onTitleClick
            })}
            onClick={onTitleClick}
            aria-label={title}
          >
            <h3
              className="mb-4 text-2xl font-semibold tracking-tight"
              style={{color: titleColor}}
            >
              {title}
            </h3>
          </button>
        )}
        <Carousel
          cards={cards}
          overtitle={card => overtitle?.(card)}
          getDescription={card => {
            if (card?.category_key)
              return t(`Categories.${card?.category_key}`);
            if (card?.category_name) return card?.category_name;
            return getDescription?.(card);
          }}
          buttonIconColor={buttonIconColor}
          onCardClick={onCardClick}
          cardWrapperClassname="px-0!"
          {...(withPlaceholderCards && {
            placeholderProps
          })}
          renderLastCard={renderLastCard}
          {...(roundCards && {
            renderCard: card => (
              <div className="h-full w-full overflow-hidden rounded-lg border bg-white shadow-sm">
                {card.logo && (
                  <div className="relative h-32 w-full overflow-hidden">
                    <img
                      src={card.logo}
                      alt={card.name || 'logo'}
                      className="h-full w-full object-cover"
                    />
                  </div>
                )}
                <div className="p-3">
                  <h4 className="mb-1 line-clamp-2 text-sm font-medium text-gray-900">
                    {card.name}
                  </h4>
                  {(getDescription?.(card) || card?.description) && (
                    <p className="line-clamp-2 text-xs text-gray-600">
                      {getDescription?.(card) || card?.description}
                    </p>
                  )}
                  {card?.subtitle && (
                    <p className="mt-1 line-clamp-2 text-xs text-gray-500">
                      {card.subtitle}
                    </p>
                  )}
                </div>
              </div>
            )
          })}
        />
      </div>
    );
  }
);

OrganisationsCarousel.displayName = 'OrganisationsCarousel';

export {OrganisationsCarousel};
export type {OrganisationsCarouselProps, OrganisationCarouselCard};
